import { useMemo } from "react";

import { cn } from "@/shared/lib/cn";

interface Props {
  /** 24-hour "HH:MM" string. Empty string is allowed (defaults to 12:00 AM display). */
  value: string;
  onChange: (value: string) => void;
  /** Minute granularity in the dropdown. Defaults to 15. */
  step?: number;
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
}

function parseValue(value: string): { h24: number; minute: number } | null {
  if (!value) return null;
  const parts = value.split(":");
  if (parts.length < 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return { h24: h, minute: m };
}

function buildValue(h12: number, minute: number, period: "AM" | "PM"): string {
  let h = h12 % 12;
  if (period === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

export function Time12hPicker({
  value,
  onChange,
  step = 15,
  ariaLabel,
  className,
  disabled,
  id,
}: Props) {
  const parsed = parseValue(value);
  const h24 = parsed?.h24 ?? 0;
  const minute = parsed?.minute ?? 0;
  const period: "AM" | "PM" = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;

  const minuteOptions = useMemo(() => {
    const out: number[] = [];
    for (let i = 0; i < 60; i += step) out.push(i);
    if (parsed && !out.includes(parsed.minute)) {
      out.push(parsed.minute);
      out.sort((a, b) => a - b);
    }
    return out;
  }, [step, parsed]);

  const setHour = (h: number) => onChange(buildValue(h, minute, period));
  const setMinute = (mm: number) => onChange(buildValue(h12, mm, period));
  const setPeriod = (p: "AM" | "PM") => onChange(buildValue(h12, minute, p));

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      id={id}
      className={cn(
        "inline-flex items-center gap-1 rounded-md border border-input bg-background px-1.5 py-1 text-sm",
        disabled && "pointer-events-none opacity-50",
        className,
      )}
    >
      <select
        value={h12}
        onChange={(e) => setHour(parseInt(e.target.value, 10))}
        disabled={disabled}
        className="rounded bg-transparent px-1 py-0.5 tabular-nums text-copy focus:outline-none focus:ring-2 focus:ring-ring"
        aria-label="Hour"
      >
        {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
      <span className="text-copy-muted">:</span>
      <select
        value={minute}
        onChange={(e) => setMinute(parseInt(e.target.value, 10))}
        disabled={disabled}
        className="rounded bg-transparent px-1 py-0.5 tabular-nums text-copy focus:outline-none focus:ring-2 focus:ring-ring"
        aria-label="Minute"
      >
        {minuteOptions.map((mm) => (
          <option key={mm} value={mm}>
            {String(mm).padStart(2, "0")}
          </option>
        ))}
      </select>
      <div
        className="ml-1 inline-flex rounded-md bg-surface-sunken p-0.5 text-xs font-medium"
        role="radiogroup"
        aria-label="AM or PM"
      >
        <button
          type="button"
          onClick={() => setPeriod("AM")}
          disabled={disabled}
          role="radio"
          aria-checked={period === "AM"}
          className={cn(
            "rounded px-2 py-0.5 transition-colors",
            period === "AM"
              ? "bg-surface text-copy shadow-card"
              : "text-copy-muted hover:text-copy",
          )}
        >
          AM
        </button>
        <button
          type="button"
          onClick={() => setPeriod("PM")}
          disabled={disabled}
          role="radio"
          aria-checked={period === "PM"}
          className={cn(
            "rounded px-2 py-0.5 transition-colors",
            period === "PM"
              ? "bg-surface text-copy shadow-card"
              : "text-copy-muted hover:text-copy",
          )}
        >
          PM
        </button>
      </div>
    </div>
  );
}
